import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { resourcesService, Resource } from '@/services/resources';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  StarIcon as StarIconSolid,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { ResourceFormModal } from '@/components/resources/ResourceFormModal';

// =====================================================
// RESOURCES ADMIN PAGE COMPONENT
// TMOS Platform - Resources Administration
// =====================================================
// Component: ResourcesAdminPage
// Description: Admin interface for managing resources
// Author: TMOS Development Team
// Date: January 30, 2025
// =====================================================

export const ResourcesAdminPage: React.FC = () => {
  const { t } = useLanguage();
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadResources();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredResources(resources);
    } else {
      const filtered = resources.filter(resource =>
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (resource.description && resource.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        resource.resource_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResources(filtered);
    }
  }, [resources, searchTerm]);

  const loadResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await resourcesService.getResources({ limit: 100 });
                  setResources(result.resources);
    } catch (err: any) {
      setError(err.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (resourceId: string, isFeatured: boolean) => {
    try {
            
      // Update the resource's featured status via API
      await resourcesService.updateResource(resourceId, {
        featured_in_widget: isFeatured
      });
      
      // Update local state after successful API call
      setResources(prev => prev.map(r => 
        r.id === resourceId ? { ...r, featured_in_widget: isFeatured } : r
      ));
      
          } catch (err: any) {
      console.error('Error toggling featured status:', err);
      setError(err.message || 'Failed to update featured status');
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await resourcesService.deleteResource(resourceId);
      setResources(prev => prev.filter(r => r.id !== resourceId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete resource');
    }
  };

  const handleEditResource = (resource: Resource) => {
    setEditingResource(resource);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = (updatedResource: Resource) => {
    setResources(prev => prev.map(r => r.id === updatedResource.id ? updatedResource : r));
    setIsEditModalOpen(false);
    setEditingResource(null);
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setEditingResource(null);
  };

  const handleCreateResource = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = (newResource: Resource) => {
    setResources(prev => [newResource, ...prev]);
    setIsCreateModalOpen(false);
  };

  const handleCreateCancel = () => {
    setIsCreateModalOpen(false);
  };

  const handleToggleActive = async (resource: Resource) => {
    try {
      const newActiveState = !resource.is_active;
      await resourcesService.updateResource(resource.id, { is_active: newActiveState });
      
      // Update local state
      setResources(prev => prev.map(r => 
        r.id === resource.id ? { ...r, is_active: newActiveState } : r
      ));
      
      alert(newActiveState ? 'Resource activated successfully' : 'Resource deactivated successfully');
    } catch (error) {
      console.error('Error toggling resource active state:', error);
      alert('Failed to update resource status');
    }
  };

  const canToggleActive = (resource: Resource) => {
    const now = new Date();
    const activeFrom = new Date(resource.active_from);
    const activeUntil = resource.active_until ? new Date(resource.active_until) : null;
    
    // Can always deactivate
    if (resource.is_active) return true;
    
    // Can only activate if within the active time window
    return activeFrom <= now && (!activeUntil || activeUntil >= now);
  };

  const isResourceActive = (resource: Resource) => {
    const now = new Date();
    const activeFrom = new Date(resource.active_from);
    const activeUntil = resource.active_until ? new Date(resource.active_until) : null;
    
    // Check if resource is within active period
    const isWithinPeriod = activeFrom <= now && (!activeUntil || activeUntil >= now);
    
    // Resource is active only if it's within the time period AND is_active is true
    return isWithinPeriod && (resource.is_active !== false);
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">{t('pages.resourcesAdmin.title')}</h1>
          <p className="mt-2 text-[var(--color-secondary-text)]">
            {t('pages.resourcesAdmin.description')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="max-w-md">
            <label htmlFor="search" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              {t('pages.resourcesAdmin.searchPlaceholder')}
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, description, or type..."
                className="block w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-secondary-text)] opacity-50 hover:opacity-100"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="mt-1 text-sm text-[var(--color-secondary-text)]">
                {t('pages.resourcesAdmin.showingResults', { filtered: filteredResources.length, total: resources.length })}
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md p-4">
            <p className="text-[var(--color-error)]">{error}</p>
          </div>
        )}

        {/* Actions Bar */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex space-x-4">
            <button
              onClick={handleCreateResource}
              className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-success)] text-[var(--color-accent-text)] rounded-md hover:opacity-90 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('pages.resourcesAdmin.createResource')}</span>
            </button>
            <button
              onClick={loadResources}
              className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90 transition-colors"
            >
              {t('pages.resourcesAdmin.refresh')}
            </button>
          </div>
          <div className="text-sm text-[var(--color-secondary-text)]">
            {resources.length} {t('pages.resourcesAdmin.resourcesTotal')}
          </div>
        </div>

        {/* Resources Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-info)]"></div>
          </div>
        ) : (
          <div className="bg-[var(--color-surface-bg)] shadow-sm rounded-lg overflow-hidden border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-secondary-bg)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.resource')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.featured')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.active')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.created')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                    {t('pages.resourcesAdmin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                {filteredResources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-[var(--color-secondary-bg)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-[var(--color-info)]/10 flex items-center justify-center">
                            {resource.resource_type === 'link' ? (
                              <span className="text-[var(--color-info)] text-sm">🔗</span>
                            ) : (
                              <span className="text-[var(--color-success)] text-sm">📄</span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-[var(--color-surface-text)]">
                            {resource.title}
                          </div>
                          {resource.description && (
                            <div className="text-sm text-[var(--color-secondary-text)] truncate max-w-xs">
                              {resource.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        resource.resource_type === 'link' 
                          ? 'bg-[var(--color-info)]/10 text-[var(--color-info)]' 
                          : 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                      }`}>
                        {resource.resource_type === 'link' ? t('pages.resourcesAdmin.link') : t('pages.resourcesAdmin.document')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleFeatured(resource.id, !resource.featured_in_widget)}
                        className="flex items-center space-x-1 text-sm hover:text-[var(--color-warning)] transition-colors"
                      >
                        {resource.featured_in_widget ? (
                          <StarIconSolid className="h-4 w-4 text-[var(--color-warning)]" />
                        ) : (
                          <StarIcon className="h-4 w-4 text-[var(--color-secondary-text)] opacity-50" />
                        )}
                        <span className={resource.featured_in_widget ? 'text-[var(--color-warning)]' : 'text-[var(--color-secondary-text)]'}>
                          {resource.featured_in_widget ? t('pages.resourcesAdmin.featured') : t('pages.resourcesAdmin.no')}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(resource)}
                        disabled={!canToggleActive(resource)}
                        className={`flex items-center space-x-1 text-sm transition-colors ${
                          isResourceActive(resource)
                            ? 'text-[var(--color-success)] hover:opacity-80'
                            : 'text-[var(--color-error)] hover:opacity-80'
                        } ${
                          !canToggleActive(resource)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        title={!canToggleActive(resource) ? 'Cannot activate outside active time window' : ''}
                      >
                        {isResourceActive(resource) ? (
                          <>
                            <CheckIcon className="h-4 w-4" />
                            <span>{t('pages.resourcesAdmin.active')}</span>
                          </>
                        ) : (
                          <>
                            <XMarkIcon className="h-4 w-4" />
                            <span>{t('pages.resourcesAdmin.no')}</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-secondary-text)]">
                      {new Date(resource.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => window.open(`/resources/${resource.id}`, '_blank')}
                        className="text-[var(--color-info)] hover:opacity-80 transition-colors"
                        title={t('pages.resourcesAdmin.viewResource')}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditResource(resource)}
                        className="text-[var(--color-success)] hover:opacity-80 transition-colors"
                        title={t('pages.resourcesAdmin.editResource')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteResource(resource.id)}
                        className="text-[var(--color-error)] hover:opacity-80 transition-colors"
                        title={t('pages.resourcesAdmin.deleteResource')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredResources.length === 0 && (
          <div className="text-center py-12">
            <div className="text-[var(--color-secondary-text)] opacity-50 text-6xl mb-4">📚</div>
            <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-2">
              {searchTerm ? t('pages.resourcesAdmin.noSearchResults') : t('pages.resourcesAdmin.noResources')}
            </h3>
            <p className="text-[var(--color-secondary-text)]">
              {searchTerm ? t('pages.resourcesAdmin.adjustSearchTerms') : t('pages.resourcesAdmin.createFirstResource')}
            </p>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && editingResource && (
          <ResourceFormModal
            isOpen={isEditModalOpen}
            onClose={handleEditCancel}
            onSuccess={() => handleEditSuccess(editingResource)}
            resource={editingResource}
            mode="edit"
          />
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <ResourceFormModal
            isOpen={isCreateModalOpen}
            onClose={handleCreateCancel}
            onSuccess={() => handleCreateSuccess({} as Resource)}
            mode="create"
          />
        )}
      </div>
    </div>
  );
};
